import React, { Suspense, lazy, useCallback, useContext } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { getCurrentUser } from '../../../../publicSession/selectors';
import { useSelector } from 'react-redux';
import retry from '../../../../utils/webpack/chunkRetry';
import {
  getModuleCount,
  getModuleDataByModuleName,
} from '@wix/da-gruser-shared/pkg/redux/selectors/modules';
import {} from '@wix/da-gruser-shared/pkg/types';
import { getGruserForDeviation } from '../../../../DeviationPage/redux/helpers';

import cn from 'classnames';

const LoadableGruser = lazy(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "dgruser" */
        '../GruserZoneLocal'
      )
  )
);

import s from './LazyGruserZone.scss';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Grid from '../../../../Grid';
import { useInView } from 'react-intersection-observer';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';

export interface Props {
  deviation: PapiDeviation;
}

export const LazyGruserZone: React.FC<Props> = ({ deviation }) => {
  const { inView, ref } = useInView({ triggerOnce: true });
  const isMounted = useIsMounted();
  const user = useSelector(getCurrentUser);
  const isOwnerView = user.userId === deviation.author?.userId;
  const gruser = getGruserForDeviation(deviation);
  const moduleCount = useSelector(state =>
    getModuleCount(state as any, gruser, 'local', 'exhibitionist')
  );
  const moduleData = useSelector(state =>
    getModuleDataByModuleName(state as any, gruser, 'exhibitionist')
  );
  const isMobile = useContext(MobileContext);
  const hasModule = moduleCount > 0;
  const renderLoadingBlock = useCallback(
    ({ height, width, index: itemIndex, style }) => {
      return (
        <div key={itemIndex} style={style}>
          <div
            className={cn(s['loading'], s['loading-block'])}
            style={{ width, height }}
          />
        </div>
      );
    },
    []
  );

  let LoadingView: React.ReactNode = null;

  if (isMobile) {
    // The loading grid for mobile
    const LoadingGrid = (
      <Grid
        lazyloadFromContext
        enableScrollOptim={false}
        containerWidth={308}
        defaultSize={{
          desktop: { width: 308, height: 308 },
          mobile: { width: 308, height: 308 },
        }}
        cookieType={MeasuredCookieType.DEVIATION_SIDEBAR}
        maxRows={3}
        breakpoints={[
          {
            maxWidth: 308,
            elementsPerRow: 3,
            elementAspectRatio: 1,
          },
        ]}
        spacing={16}
        elementCount={moduleData?.deviations?.length}
        renderElement={renderLoadingBlock}
      />
    );

    // Need slightly different views for owner vs visitor as owner needs to see the add button
    LoadingView = isOwnerView ? (
      <div
        ref={ref}
        className={cn(
          !hasModule && s['lazy-owner-loading-no-module'],
          hasModule && s['lazy-owner-loading-with-module']
        )}
      >
        {hasModule && (
          <>
            <div className={cn(s['loading'], s['loading-title'])}></div>
            {LoadingGrid}
          </>
        )}
      </div>
    ) : (
      <div ref={ref} className={cn(s['lazy-visitor-loading-with-module'])}>
        <>
          <div className={cn(s['loading'], s['loading-title'])}></div>
          {LoadingGrid}
        </>
      </div>
    );
  } else {
    // Desktop
    // Need slightly different views for owner vs visitor as owner needs to see the add button
    LoadingView = isOwnerView ? (
      <div
        ref={ref}
        className={cn(
          !hasModule && s['lazy-owner-loading-no-module'],
          hasModule && s['lazy-owner-loading-with-module']
        )}
      >
        {hasModule && (
          <>
            <div className={cn(s['loading'], s['loading-title'])}></div>
            <div className={cn(s['loading'], s['loading-block'])}></div>
          </>
        )}
      </div>
    ) : (
      <div ref={ref} className={cn(s['lazy-visitor-loading-with-module'])}>
        <>
          <div className={cn(s['loading'], s['loading-title'])}></div>
          <div className={cn(s['loading'], s['loading-block'])}></div>
        </>
      </div>
    );
  }

  if (!isMounted || !inView) {
    // Don't even load for visitor when no module returned
    // Dont load for owener on mobile as we don't show controls
    if ((!isOwnerView && !hasModule) || (isMobile && !hasModule)) {
      return null;
    }
    return LoadingView;
  }

  return (
    <Suspense fallback={LoadingView}>
      <LoadableGruser deviation={deviation} />
    </Suspense>
  );
};
LazyGruserZone.displayName = 'LazyGruserZone';

export default LazyGruserZone;
